import React from "react";
import { motion } from "framer-motion";
import { PRODUCTS } from "../data/content";
import PogLogo from "./PogLogo";
import ShareBar from "./ShareBar";

const Products = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative py-32 md:py-56 bg-[#1E1712] text-[#EFE7D6] overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-8 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="mb-24 md:mb-32"
        >
          <div className="flex items-center gap-6 mb-10">
            <span className="counter">04 — Range</span>
            <span className="w-16 hairline-strong h-px inline-block" />
            <span className="overline">The Bottles</span>
          </div>
          <h2
            className="display-tight text-[3rem] sm:text-6xl md:text-8xl max-w-5xl"
            data-testid="products-headline"
          >
            Two vessels.
            <br />
            <span className="italic font-light text-[#DCCFB4]">One island.</span>
          </h2>
          <p className="font-body text-xl md:text-2xl text-[#DCCFB4]/80 mt-10 max-w-2xl leading-[1.5]">
            Built around the icon. Crafted for the moment.
          </p>
        </motion.div>

        <div className="space-y-40 md:space-y-56">
          {PRODUCTS.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className={`grid md:grid-cols-12 gap-14 md:gap-20 items-center ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
              data-testid={`product-${p.id}`}
            >
              {/* Product image */}
              <div className="md:col-span-6 [direction:ltr]">
                <div
                  className="relative aspect-[4/5] overflow-hidden"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 40%, #2a1f18 0%, #0f0a07 75%)",
                  }}
                >
                  {/* Faint sunburst behind */}
                  <div className="absolute inset-0 opacity-25 sunburst-ghost" />
                  <div className="absolute inset-0 grain pointer-events-none" />

                  <motion.img
                    src={p.image}
                    alt={p.alt || p.name}
                    loading="lazy"
                    initial={{ scale: 1.05, opacity: 0.9 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: "easeOut" }}
                    className="absolute inset-0 w-full h-full object-contain p-10 md:p-14 drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
                  />

                  {/* Corner meta */}
                  <div className="absolute top-6 left-6 flex items-center gap-3 z-10">
                    <span className="counter text-[#EFE7D6]/70">
                      0{i + 1}
                    </span>
                    <span className="w-8 hairline-strong h-px inline-block" />
                    <span className="overline text-[#EFE7D6]/70">
                      {p.badge}
                    </span>
                  </div>
                </div>
              </div>

              {/* Product info */}
              <div className="md:col-span-6 [direction:ltr]">
                <div className="flex items-center gap-4 mb-8">
                  <PogLogo size={44} />
                  <span className="font-body text-lg text-[#DCCFB4]/75">
                    {p.subtitle}
                  </span>
                </div>

                <h3 className="display-tight text-5xl md:text-7xl mb-10">
                  {p.name}
                </h3>

                <div className="flex gap-6 mb-12 text-[10px] tracking-[0.28em] uppercase text-[#DCCFB4]/70 font-body">
                  <span>{p.size}</span>
                  <span className="text-[#DCCFB4]/30">/</span>
                  <span>{p.abv}</span>
                </div>

                <p className="font-body text-xl md:text-2xl text-[#EFE7D6]/90 leading-[1.5] mb-12 max-w-lg">
                  {p.blurb}
                </p>

                <dl className="space-y-0 mb-12 max-w-lg">
                  {p.notes.map((n) => (
                    <div
                      key={n.k}
                      className="grid grid-cols-12 gap-4 py-5 border-t border-[#EFE7D6]/12"
                    >
                      <dt className="col-span-4 overline text-[#E85A15]/90">
                        {n.k}
                      </dt>
                      <dd className="col-span-8 font-body text-base text-[#EFE7D6]/85 font-light leading-relaxed">
                        {n.v}
                      </dd>
                    </div>
                  ))}
                  <span className="hairline block" />
                </dl>

                <button
                  onClick={() =>
                    document
                      .getElementById("inquire")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="btn-primary"
                  data-testid={`product-${p.id}-cta`}
                >
                  Carry POG
                </button>

                <div className="mt-10">
                  <ShareBar
                    url={`https://pogspirits.com/#product-${p.id}`}
                    title={`${p.name} — ${p.subtitle} | POG Spirits`}
                    testid={`product-${p.id}-share`}
                  />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
